import React, { useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useScene } from "../components/scene/SceneContext";
import SceneBackground from "../components/scene/SceneBackground";
import "./Chat.css";
import { useTranslation } from "react-i18next";

function extractGroupContextFromToolResult(toolResult) {
  const out = {};
  const content = toolResult && toolResult.content;
  if (!Array.isArray(content)) return out;

  for (const item of content) {
    const text = item && item.text;
    if (typeof text !== "string" || !text.trim()) continue;
    let parsed = null;
    try {
      parsed = JSON.parse(text);
    } catch {
      continue;
    }
    if (!parsed || typeof parsed !== "object") continue;
    const groupUrl = parsed?.context?.frontendGroupUrl;
    const groupId =
      parsed?.payload?.id ||
      parsed?.payload?.groupId ||
      parsed?.groupId ||
      parsed?.payload?.group?.id ||
      null;
    if (typeof groupUrl === "string" && groupUrl) out.activeGroupUrl = groupUrl;
    if (typeof groupId === "string" && groupId) out.activeGroupId = groupId;
    if (out.activeGroupUrl || out.activeGroupId) return out;
  }
  return out;
}

function extractGroupUrlFromText(text) {
  const s = String(text || "");
  // Match either an absolute URL or a relative /group/<uuid>
  const m =
    s.match(/https?:\/\/[^\s]+\/group\/([0-9a-fA-F-]{36})/i) ||
    s.match(/\/group\/([0-9a-fA-F-]{36})/i);
  if (!m) return null;
  const groupId = m[1];
  const fullUrl = m[0].startsWith("http") ? m[0] : null;
  return { groupId, fullUrl };
}

function getChatBaseUrl() {
  // If unset, use same-origin relative requests (recommended for prod via reverse proxy).
  const configured = String(process.env.REACT_APP_MCP_HTTP_URL || "").replace(/\/+$/, "");
  if (configured) return configured;
  // Dev-friendly default: MCP http server typically runs on 3002.
  if (process.env.NODE_ENV === "development") return "http://localhost:3002";
  return "";
}

async function streamChatMessage(params) {
  const { sessionId, message, onEvent, signal } = params;
  const base = getChatBaseUrl();
  const url = `${base}/chat/stream`;

  const resp = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ sessionId, message }),
    signal,
  });

  if (!resp.ok || !resp.body) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `HTTP ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames end with \n\n
      const parts = buffer.split("\n\n");
      buffer = parts.pop() || "";

      for (const frame of parts) {
        const lines = frame.split("\n");
        const dataLines = lines
          .filter((l) => l.startsWith("data:"))
          .map((l) => l.slice("data:".length).trim());
        if (!dataLines.length) continue;
        const data = dataLines.join("\n");
        try {
          const obj = JSON.parse(data);
          if (typeof onEvent === "function") onEvent(obj);
        } catch {
          // ignore malformed frames
        }
      }
    }
  } catch (e) {
    if (signal && signal.aborted) return;
    throw e;
  }
}

export default function Chat() {
  const history = useHistory();
  const { variant } = useScene();
  const { t } = useTranslation();

  const [sessionId, setSessionId] = useState(null);
  const [activeGroupUrl, setActiveGroupUrl] = useState(null);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [streamStatus, setStreamStatus] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        t("chat.welcome"),
    },
  ]);

  const listRef = useRef(null);

  const canSend = useMemo(() => input.trim() && !isSending, [input, isSending]);

  const scrollToBottom = () => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isSending) return;

    setError(null);
    setIsSending(true);
    setInput("");

    const next = [...messages, { role: "user", content: text }];
    setMessages(next);

    // allow DOM update first
    setTimeout(scrollToBottom, 0);

    try {
      const abort = new AbortController();
      let finalContent = null;
      let finalized = false;

      await streamChatMessage({
        sessionId,
        message: text,
        signal: abort.signal,
        onEvent: (evt) => {
          if (!evt || typeof evt !== "object") return;
          if (evt.type === "meta") {
            if (evt.sessionId && !sessionId) setSessionId(evt.sessionId);
            if (evt.activeGroupUrl) setActiveGroupUrl(String(evt.activeGroupUrl));
            if (evt.activeGroupId) setActiveGroupId(String(evt.activeGroupId));
          }
          if (evt.type === "context") {
            if (evt.activeGroupUrl) setActiveGroupUrl(String(evt.activeGroupUrl));
            if (evt.activeGroupId) setActiveGroupId(String(evt.activeGroupId));
          }
          if (evt.type === "status" && typeof evt.message === "string") setStreamStatus(evt.message);
          if (evt.type === "tool_call" && evt.name) setStreamStatus(t("chat.status_calling", { tool: evt.name }));
          if (evt.type === "tool_result" && evt.name) setStreamStatus(t("chat.status_finished", { tool: evt.name }));
          if (evt.type === "tool_result" && evt.result) {
            const ctx = extractGroupContextFromToolResult(evt.result);
            if (ctx.activeGroupUrl) setActiveGroupUrl(String(ctx.activeGroupUrl));
            if (ctx.activeGroupId) setActiveGroupId(String(ctx.activeGroupId));
          }
          if (evt.type === "error" && evt.detail) setError(String(evt.detail));
          if (evt.type === "final" && typeof evt.content === "string") {
            finalContent = evt.content;
            finalized = true;
            const assistantText = String(evt.content || "").trim();
            setMessages((prev) => [...prev, { role: "assistant", content: assistantText || "(empty response)" }]);
            setIsSending(false);
            setStreamStatus(null);
            setTimeout(scrollToBottom, 0);
            abort.abort();
            // Fallback: if server context didn't arrive, parse a group link from text.
            const parsed = extractGroupUrlFromText(evt.content);
            if (parsed?.fullUrl) setActiveGroupUrl(parsed.fullUrl);
            if (parsed?.groupId) setActiveGroupId(parsed.groupId);
          }
        },
      });

      if (!finalized) {
        setStreamStatus(null);
        const assistantText = String(finalContent || "").trim();
        setMessages((prev) => [...prev, { role: "assistant", content: assistantText || "(empty response)" }]);
        setTimeout(scrollToBottom, 0);
      }
    } catch (e) {
      setError(String(e?.message || e));
      // Put the unsent text back for convenience
      setInput(text);
    } finally {
      setIsSending(false);
      setStreamStatus(null);
    }
  };

  return (
    <div className={`d-flex flex-column inner-height as-theme chat chat--${variant}`}>
      <SceneBackground effectsProps={{ preset: "landing", showAnimals: true }} />
      <Navbar
        showActionBtn
        actionBtnIconClassName="bi-arrow-left"
        handleActionBtnOnClick={() => history.push(`${process.env.PUBLIC_URL}/`)}
        rightSlot={
          <span className="small text-muted" title="MCP Chat">
            {t("general.ai_chat")}
          </span>
        }
      />

      <div className="container-xl flex-grow-1 d-flex flex-column py-3 chat-content" style={{ minHeight: 0 }}>
        <div className="card flex-grow-1 d-flex flex-column chat-card" style={{ minHeight: 0 }}>
          <div className="card-header d-flex align-items-center justify-content-between">
            <div className="fw-semibold">{t("chat.assistant_name")}</div>
            <div className="d-flex align-items-center gap-2">
              {activeGroupUrl || activeGroupId ? (
                <a
                  className="btn btn-sm as-btn-primary-gradient chat-open-group-btn"
                  href={activeGroupUrl || `${process.env.PUBLIC_URL}/group/${activeGroupId}`}
                  title={t("chat.open_group_title")}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="bi bi-box-arrow-up-right me-2" />
                  {t("chat.open_group")}
                </a>
              ) : null}
              <div className="text-muted small">{t("general.ai_chat")}</div>
            </div>
          </div>

          <div
            ref={listRef}
            className="card-body"
            style={{ overflowY: "auto" }}
          >
            {messages.map((m, idx) => (
              <div key={idx} className={`mb-3 d-flex ${m.role === "user" ? "justify-content-end" : "justify-content-start"}`}>
                <div
                  className={`p-2 rounded ${m.role === "user" ? "bg-primary text-white" : "bg-light"}`}
                  style={{ maxWidth: "85%", whiteSpace: "pre-wrap" }}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {isSending ? (
              <div className="text-muted small">{streamStatus || t("chat.thinking")}</div>
            ) : null}

            {error ? (
              <div className="alert alert-danger mt-2 mb-0">
                <div className="fw-semibold">{t("chat.error_title")}</div>
                <div className="small" style={{ whiteSpace: "pre-wrap" }}>
                  {error}
                </div>
                <div className="small mt-2">
                  {t("chat.error_tip")}
                </div>
              </div>
            ) : null}
          </div>

          <div className="card-footer">
            <div className="d-flex gap-2 chat-input-row">
              <textarea
                className="form-control"
                rows={2}
                placeholder={t("chat.input_placeholder")}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={isSending}
              />
              <button className="btn btn-primary chat-send-btn" onClick={handleSend} disabled={!canSend}>
                {t("chat.send")}
              </button>
            </div>
            <div className="text-muted small mt-2">
              {t("chat.hint_enter")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

